import { TableFilterItem } from 'src/presentations/components/molecules/table/columnHeader/types';
import { MRT_SortingState } from 'material-react-table';
import { formatNumber } from 'src/utils/utility';

/**
 * Shared boolean accessor functions for common charge fields.
 * Used across all charge table types to convert booleans to 'True'/'False' strings for filtering.
 */
export const commonBooleanAccessors = {
  accessorial: (item: any) => (item.accessorial ? 'True' : 'False'),
  approval: (item: any) => (item.approval ? 'True' : 'False'),
  outlay: (item: any) => (item.outlay ? 'True' : 'False'),
  deviationExcluded: (item: any) => (item.deviationExcluded ? 'True' : 'False'),
};

/**
 * Creates filter options with counts from charge arrays
 * @param data Array of charge items from contract flow
 * @param fieldName Field to extract values from for filtering
 * @param accessorFn Optional custom function to extract values (useful for boolean fields displayed as True/False)
 * @returns Array of filter items with counts for use with TableHeader component
 */
export const createFilterOptions = <T>(data: T[], fieldName: keyof T | string, accessorFn?: (item: T) => string): TableFilterItem[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Count occurrences of each value
  const valueCounts: Record<string, number> = {};
  // Track if this field contains numeric values
  let isNumericField = false;

  data.forEach((item) => {
    let value;

    // Use accessor function if provided, otherwise try to access the property directly
    if (accessorFn) {
      value = accessorFn(item);
    } else {
      value = item[fieldName as keyof T];
    }

    // Skip undefined or null values
    if (value === undefined || value === null) {
      return;
    }

    // Check if the field is numeric for proper formatting
    if (typeof value === 'number' || (!Number.isNaN(parseFloat(String(value))) && Number.isFinite(Number(value)))) {
      isNumericField = true;
    }

    const stringValue = String(value);

    if (valueCounts[stringValue]) {
      valueCounts[stringValue] += 1;
    } else {
      valueCounts[stringValue] = 1;
    }
  });

  // Convert to TableFilterItem array
  const filterItems = Object.entries(valueCounts).map(([value, count]) => {
    let displayLabel = value || 'N/A';

    // For numeric fields, format them with 2 decimal places
    if (isNumericField && value !== '' && !Number.isNaN(parseFloat(value))) {
      displayLabel = formatNumber(parseFloat(value));
    }

    return {
      id: value,
      label: displayLabel,
      count,
      // Store the numeric value for sorting if applicable
      numericValue: isNumericField && !Number.isNaN(parseFloat(value)) ? parseFloat(value) : undefined,
    };
  });

  // Sort fields correctly based on their type
  filterItems.sort((a, b) => {
    if (isNumericField) {
      // Handle the case where numericValue might be undefined
      if (a.numericValue === undefined && b.numericValue === undefined) {
        // If both are undefined, sort by string value
        return a.id.localeCompare(b.id);
      }
      if (a.numericValue === undefined) return 1;
      if (b.numericValue === undefined) return -1;

      return a.numericValue - b.numericValue;
    }

    // For string fields, use localeCompare for proper alphabetical sorting
    // Handle null values (put them at the beginning)
    if (a.id === null || a.id === 'null') return -1;
    if (b.id === null || b.id === 'null') return 1;
    return a.id.localeCompare(b.id);
  });

  return filterItems;
};

/**
 * Apply filters to charge data
 * @param data Array of charge items from contract flow
 * @param filters Object with field names as keys and arrays of selected values as values
 * @param accessors Optional object with field names as keys and accessor functions as values
 * @returns Filtered array of charge items
 */
export const applyFilters = <T>(data: T[], filters: Record<string, string[]>, accessors?: Record<string, (item: T) => string>): T[] => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  if (!filters || Object.keys(filters).length === 0) {
    return data;
  }

  return data.filter((item) =>
    // Item must match all active filters
    Object.entries(filters).every(([field, selectedValues]) => {
      // Skip empty filter arrays (no selection means no filtering)
      if (!selectedValues.length) {
        return true;
      }

      let itemValue;

      // Use accessor function if provided for this field
      if (accessors && accessors[field]) {
        itemValue = accessors[field](item);
      } else {
        // Get the field value from the item directly
        itemValue = item[field as keyof T];
      }

      // If the value is not set, it doesn't match any filter
      if (itemValue === undefined || itemValue === null) {
        return false;
      }

      // Convert to string for comparison
      const stringValue = String(itemValue);

      // Check if the value is in the selected values array
      return selectedValues.includes(stringValue);
    })
  );
};

/**
 * Apply sorting to dataA
 * @param data Array of items to sort
 * @param sorting Material React Table sorting state
 * @returns Sorted array of items
 */
export const applySorting = <T>(data: T[], sorting?: MRT_SortingState): T[] => {
  if (!data || !Array.isArray(data) || !sorting || sorting.length === 0) {
    return data;
  }

  // Create a copy of the data to avoid mutating the original
  const sortedData = [...data];

  // Get the first sorting criteria (MRT supports multiple, but we'll use the first one)
  const { id, desc } = sorting[0];

  return sortedData.sort((a, b) => {
    const valueA = a[id as keyof T];
    const valueB = b[id as keyof T];

    // Handle undefined or null values
    if (valueA === undefined || valueA === null) return desc ? -1 : 1;
    if (valueB === undefined || valueB === null) return desc ? 1 : -1;

    // Handle numeric values
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return desc ? valueB - valueA : valueA - valueB;
    }

    // Handle string values (case-insensitive)
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();

    if (strA < strB) return desc ? 1 : -1;
    if (strA > strB) return desc ? -1 : 1;
    return 0;
  });
};

/**
 * Apply both filtering and sorting to data
 * @param data Array of items
 * @param filters Filter criteria
 * @param accessors Optional accessors for filtering
 * @param sorting Optional sorting state
 * @returns Filtered and sorted array of items
 */
export const applyFiltersAndSorting = <T>(
  data: T[],
  filters: Record<string, string[]>,
  accessors?: Record<string, (item: T) => string>,
  sorting?: MRT_SortingState
): T[] => {
  // First apply filters
  const filteredData = applyFilters(data, filters, accessors);

  // Then apply sorting
  return applySorting(filteredData, sorting);
};
